import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { mediaPortalApi, exercisesApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Radio, Link2, Unlink, Globe, Send, Clock, Eye,
  Settings, Newspaper, RefreshCw, ExternalLink, BarChart3,
  TrendingUp, MessageSquare, Share2, Heart, Play, Zap,
  CheckCircle2, Circle, ArrowRight, ChevronDown, ChevronUp,
  Plus, CalendarClock,
} from 'lucide-react';

// ==================== ANALYTICS CARDS ====================

const AnalyticsCards = ({ analytics }) => {
  if (!analytics) return null;
  const cards = [
    { label: 'Total Posts', value: analytics.total_posts, icon: Newspaper, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Published', value: analytics.published, icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    { label: 'Scheduled', value: analytics.scheduled, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'Drafts', value: analytics.draft, icon: Circle, color: 'text-muted-foreground', bg: 'bg-muted-foreground/10' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mp-analytics-cards">
      {cards.map(c => (
        <div key={c.label} className="p-4 rounded-xl border bg-card/50 backdrop-blur">
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-1.5 rounded-lg ${c.bg}`}><c.icon className={`w-4 h-4 ${c.color}`} /></div>
          </div>
          <p className="text-2xl font-bold tracking-tight">{c.value}</p>
          <p className="text-xs text-muted-foreground">{c.label}</p>
        </div>
      ))}
    </div>
  );
};

// ==================== ENGAGEMENT METRICS ====================

const EngagementMetrics = ({ engagement }) => {
  if (!engagement) return null;
  const metrics = [
    { label: 'Views', value: engagement.views, icon: Eye, color: 'text-blue-400' },
    { label: 'Likes', value: engagement.likes, icon: Heart, color: 'text-rose-400' },
    { label: 'Comments', value: engagement.comments, icon: MessageSquare, color: 'text-purple-400' },
    { label: 'Shares', value: engagement.shares, icon: Share2, color: 'text-teal-400' },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-500" /> Engagement Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-3" data-testid="mp-engagement-metrics">
          {metrics.map(m => (
            <div key={m.label} className="text-center">
              <m.icon className={`w-5 h-5 mx-auto mb-1 ${m.color}`} />
              <p className="text-lg font-bold">{m.value.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">{m.label}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== PLATFORM BREAKDOWN ====================

const PlatformBreakdown = ({ byPlatform, platforms }) => {
  if (!byPlatform || Object.keys(byPlatform).length === 0) return null;
  const total = Object.values(byPlatform).reduce((a, b) => a + b, 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Globe className="w-4 h-4 text-blue-500" /> By Platform
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2" data-testid="mp-platform-breakdown">
        {Object.entries(byPlatform).sort((a, b) => b[1] - a[1]).map(([plat, count]) => {
          const p = platforms.find(x => x.name === plat);
          const pct = total ? Math.round((count / total) * 100) : 0;
          return (
            <div key={plat} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p?.color || '#666' }} />
              <span className="text-sm flex-1 truncate">{p?.display_name || plat}</span>
              <span className="text-sm font-medium">{count}</span>
              <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: p?.color || '#666' }} />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// ==================== POST QUEUE ====================

const PostQueue = ({ posts, platforms, onPublish, publishingId }) => {
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  const filtered = posts.filter(p => filter === 'all' || p.status === filter);

  const statusConfig = {
    published: { label: 'Published', color: 'bg-emerald-500', badge: 'default' },
    scheduled: { label: 'Scheduled', color: 'bg-amber-500', badge: 'outline' },
    draft: { label: 'Draft', color: 'bg-muted-foreground', badge: 'secondary' },
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Newspaper className="w-4 h-4" /> Post Queue ({posts.length})
          </CardTitle>
          <div className="flex gap-1">
            {['all', 'published', 'scheduled', 'draft'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2 py-1 rounded text-xs transition-colors ${
                  filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                data-testid={`mp-filter-${f}`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Send className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No posts {filter !== 'all' ? `with status "${filter}"` : 'yet'}</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
            {filtered.map(post => {
              const plat = platforms.find(p => p.name === post.platform);
              const sc = statusConfig[post.status] || statusConfig.draft;
              const isExpanded = expanded === post.post_id;

              return (
                <div
                  key={post.post_id}
                  className="group rounded-lg border bg-card hover:border-primary/30 transition-colors overflow-hidden"
                  data-testid={`mp-post-${post.post_id}`}
                >
                  <div className="p-3 flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : post.post_id)}>
                    {/* Platform dot */}
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: (plat?.color || '#666') + '18' }}
                    >
                      <Globe className="w-4 h-4" style={{ color: plat?.color || '#666' }} />
                    </div>

                    {/* Content preview */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{post.title || post.content?.slice(0, 80) || 'Untitled'}</p>
                      <p className="text-xs text-muted-foreground">
                        {plat?.display_name || post.platform} &middot; {post.author?.name || 'Unknown'}
                        {post.schedule?.publish_datetime && (
                          <> &middot; <Clock className="w-3 h-3 inline" /> {new Date(post.schedule.publish_datetime).toLocaleString()}</>
                        )}
                      </p>
                    </div>

                    {/* Status + Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant={sc.badge} className="text-xs">{sc.label}</Badge>
                      {post.status !== 'published' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                          onClick={(e) => { e.stopPropagation(); onPublish(post.post_id); }}
                          disabled={publishingId === post.post_id}
                          data-testid={`mp-publish-post-${post.post_id}`}
                        >
                          {publishingId === post.post_id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <><Play className="w-3.5 h-3.5 mr-1" /> Publish</>
                          )}
                        </Button>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t pt-2 space-y-2 text-sm">
                      <p className="text-muted-foreground whitespace-pre-wrap">{post.content}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Created: {new Date(post.created_at).toLocaleString()}</span>
                        {post.ems_inject_id && (
                          <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> From inject</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== REAL-TIME STATUS INDICATOR ====================

const StatusIndicator = ({ bridgeStatus, exercise }) => {
  if (!bridgeStatus?.linked) return null;

  const mpStatus = bridgeStatus.mp_exercise_status;
  const isLive = mpStatus === 'enabled';
  const emsStatus = exercise?.status;

  return (
    <div className="flex items-center gap-4 p-3 rounded-xl border bg-card/50" data-testid="mp-status-indicator">
      {/* Live indicator */}
      <div className="flex items-center gap-2">
        <span className={`relative flex h-3 w-3`}>
          {isLive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isLive ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
        </span>
        <span className="text-sm font-medium">{isLive ? 'LIVE' : mpStatus?.toUpperCase() || 'OFFLINE'}</span>
      </div>
      <div className="h-4 w-px bg-border" />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Radio className="w-3.5 h-3.5" />
        <span>{bridgeStatus.mp_exercise_name}</span>
      </div>
      <div className="h-4 w-px bg-border" />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span>EMS: <Badge variant={emsStatus === 'active' ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">{emsStatus}</Badge></span>
      </div>
      <div className="h-4 w-px bg-border" />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Newspaper className="w-3.5 h-3.5" />
        <span>{bridgeStatus.post_count} posts</span>
      </div>
      {bridgeStatus.auto_publish && (
        <>
          <div className="h-4 w-px bg-border" />
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-emerald-600 border-emerald-300">
            <Zap className="w-3 h-3 mr-0.5" /> Auto
          </Badge>
        </>
      )}
    </div>
  );
};

// ==================== CREATE POST FORM ====================

const CreatePostForm = ({ exerciseId, platforms, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '',
    content: '',
    platform: '',
    scheduled: false,
    publish_datetime: '',
  });

  const handleCreate = async () => {
    if (!form.content.trim()) {
      toast.error('Post content is required');
      return;
    }
    setCreating(true);
    try {
      await mediaPortalApi.createPost(exerciseId, form);
      toast.success(form.scheduled ? 'Post scheduled!' : 'Post published!');
      setForm({ title: '', content: '', platform: '', scheduled: false, publish_datetime: '' });
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(err.message || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="mp-create-post-btn">
        <Plus className="w-4 h-4 mr-2" /> New Post
      </Button>
    );
  }

  return (
    <Card className="border-primary/20" data-testid="mp-create-post-form">
      <CardContent className="pt-5 space-y-3">
        <h3 className="font-medium flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Create Media Post
        </h3>
        <Input
          placeholder="Post title (optional)"
          value={form.title}
          onChange={e => setForm({ ...form, title: e.target.value })}
          data-testid="mp-post-title"
        />
        <Textarea
          placeholder="Post content..."
          rows={3}
          value={form.content}
          onChange={e => setForm({ ...form, content: e.target.value })}
          data-testid="mp-post-content"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Platform</Label>
            <Select value={form.platform} onValueChange={v => setForm({ ...form, platform: v })}>
              <SelectTrigger data-testid="mp-post-platform">
                <SelectValue placeholder="Use default" />
              </SelectTrigger>
              <SelectContent>
                {platforms.map(p => (
                  <SelectItem key={p.platform_id} value={p.name}>{p.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-5">
            <Switch
              checked={form.scheduled}
              onCheckedChange={v => setForm({ ...form, scheduled: v })}
              data-testid="mp-post-schedule-toggle"
            />
            <Label className="text-xs flex items-center gap-1">
              <CalendarClock className="w-3.5 h-3.5" /> Schedule
            </Label>
          </div>
          {form.scheduled && (
            <div>
              <Label className="text-xs">Publish Date & Time</Label>
              <Input
                type="datetime-local"
                value={form.publish_datetime}
                onChange={e => setForm({ ...form, publish_datetime: e.target.value })}
                data-testid="mp-post-datetime"
              />
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={handleCreate} disabled={creating} data-testid="mp-post-submit">
            {creating ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            {form.scheduled ? 'Schedule Post' : 'Publish Now'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== MAIN PAGE ====================

const MediaPortalPage = () => {
  const { user } = useAuth();
  const [emsExercises, setEmsExercises] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [postQueue, setPostQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const refreshRef = useRef(null);

  const isAdmin = user?.roles?.includes('super_admin');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [exercises, plats] = await Promise.all([
        exercisesApi.getAll(),
        mediaPortalApi.listPlatforms(),
      ]);
      setEmsExercises(exercises);
      setPlatforms(plats);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadExerciseDetails = useCallback(async (exerciseId) => {
    try {
      const [status, sett, analyticsData, queue] = await Promise.all([
        mediaPortalApi.getBridgeStatus(exerciseId),
        mediaPortalApi.getSettings(exerciseId),
        mediaPortalApi.getAnalytics(exerciseId),
        mediaPortalApi.getPostQueue(exerciseId),
      ]);
      setBridgeStatus(status);
      setSettings(sett);
      setAnalytics(analyticsData);
      setPostQueue(queue.posts || []);
    } catch {
      setBridgeStatus(null);
      setSettings(null);
      setAnalytics(null);
      setPostQueue([]);
    }
  }, []);

  useEffect(() => {
    if (selectedExercise) {
      loadExerciseDetails(selectedExercise);
      // Auto-refresh every 15 seconds
      refreshRef.current = setInterval(() => loadExerciseDetails(selectedExercise), 15000);
      return () => clearInterval(refreshRef.current);
    }
  }, [selectedExercise, loadExerciseDetails]);

  const handleLink = async () => {
    if (!selectedExercise) return;
    setLinking(true);
    try {
      await mediaPortalApi.linkExercise(selectedExercise);
      toast.success('Media Portal exercise created and linked!');
      await loadExerciseDetails(selectedExercise);
    } catch (err) {
      toast.error(err.message || 'Failed to link exercise');
    } finally {
      setLinking(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!selectedExercise || !settings) return;
    try {
      await mediaPortalApi.updateSettings(selectedExercise, settings);
      toast.success('Settings saved');
      setSettingsOpen(false);
      await loadExerciseDetails(selectedExercise);
    } catch (err) {
      toast.error(err.message || 'Failed to save settings');
    }
  };

  const handlePublishPost = async (postId) => {
    setPublishingId(postId);
    try {
      await mediaPortalApi.publishPost(postId);
      toast.success('Post published!');
      await loadExerciseDetails(selectedExercise);
    } catch (err) {
      toast.error(err.message || 'Failed to publish post');
    } finally {
      setPublishingId(null);
    }
  };

  const selectedEx = emsExercises.find(e => e.exercise_id === selectedExercise);

  return (
    <DashboardLayout title="Media Portal">
      <div className="space-y-5" data-testid="media-portal-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            Command center for media simulation — monitor, schedule, and publish posts across platforms.
          </p>
          <Button variant="outline" size="sm" onClick={() => { loadData(); if (selectedExercise) loadExerciseDetails(selectedExercise); }} data-testid="mp-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Exercise Selector */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Select value={selectedExercise || ''} onValueChange={setSelectedExercise}>
              <SelectTrigger data-testid="mp-exercise-select" className="h-11">
                <SelectValue placeholder="Select an exercise to manage media..." />
              </SelectTrigger>
              <SelectContent>
                {emsExercises.map(ex => (
                  <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                    {ex.name} <span className="text-muted-foreground ml-1">({ex.status})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedExercise && bridgeStatus && !bridgeStatus.linked && isAdmin && (
            <Button onClick={handleLink} disabled={linking} className="h-11" data-testid="mp-link-btn">
              <Link2 className="w-4 h-4 mr-2" />
              {linking ? 'Linking...' : 'Link to Media Portal'}
            </Button>
          )}
          {selectedExercise && bridgeStatus?.linked && isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(!settingsOpen)} className="h-11" data-testid="mp-settings-btn">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
          )}
        </div>

        {/* Not Linked State */}
        {selectedExercise && bridgeStatus && !bridgeStatus.linked && (
          <div className="p-12 text-center border rounded-xl bg-muted/10">
            <Unlink className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
            <h3 className="text-lg font-semibold mb-1">Not Linked</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This exercise isn't connected to the Media Portal yet.
              {isAdmin ? ' Click "Link to Media Portal" to create the connection.' : ' Ask an admin to link it.'}
            </p>
          </div>
        )}

        {/* Linked Exercise Dashboard */}
        {selectedExercise && bridgeStatus?.linked && (
          <div className="space-y-4">
            {/* Real-time Status Bar */}
            <StatusIndicator bridgeStatus={bridgeStatus} exercise={selectedEx} />

            {/* Settings Panel */}
            {settingsOpen && settings && (
              <Card className="border-primary/20">
                <CardContent className="pt-5 space-y-4">
                  <h3 className="font-medium flex items-center gap-2"><Settings className="w-4 h-4" /> Integration Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs">Default Platform</Label>
                      <Select
                        value={settings.default_platform || ''}
                        onValueChange={v => setSettings({ ...settings, default_platform: v })}
                      >
                        <SelectTrigger data-testid="mp-default-platform">
                          <SelectValue placeholder="Select platform..." />
                        </SelectTrigger>
                        <SelectContent>
                          {platforms.map(p => (
                            <SelectItem key={p.platform_id} value={p.name}>{p.display_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-3 pt-5">
                      <Switch
                        checked={settings.auto_publish || false}
                        onCheckedChange={v => setSettings({ ...settings, auto_publish: v })}
                        data-testid="mp-auto-publish-toggle"
                      />
                      <div>
                        <Label className="text-xs">Auto-publish on inject trigger</Label>
                        <p className="text-[10px] text-muted-foreground">Triggered injects auto-post to Media Portal</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Default Author Name</Label>
                      <Input
                        value={settings.default_author?.name || ''}
                        onChange={e => setSettings({
                          ...settings,
                          default_author: { ...settings.default_author, name: e.target.value }
                        })}
                        data-testid="mp-author-name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs">Author Handle</Label>
                      <Input
                        value={settings.default_author?.handle || ''}
                        onChange={e => setSettings({
                          ...settings,
                          default_author: { ...settings.default_author, handle: e.target.value }
                        })}
                        data-testid="mp-author-handle"
                      />
                    </div>
                    <div />
                    <div className="flex items-end">
                      <Button onClick={handleSaveSettings} className="w-full" data-testid="mp-save-settings-btn">
                        Save Settings
                      </Button>
                    </div>
                  </div>
                  {/* Guest Access */}
                  {bridgeStatus.guest_token && (
                    <div className="p-3 rounded-lg bg-muted/50 flex items-center gap-2 text-sm">
                      <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-muted-foreground">Guest Access Token:</span>
                      <code className="text-xs bg-background px-2 py-0.5 rounded border" data-testid="mp-guest-token">
                        {bridgeStatus.guest_token}
                      </code>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Analytics + Engagement Row */}
            <AnalyticsCards analytics={analytics} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Engagement */}
              <div className="md:col-span-2">
                <EngagementMetrics engagement={analytics?.engagement} />
              </div>
              {/* Platform Breakdown */}
              <PlatformBreakdown byPlatform={analytics?.by_platform} platforms={platforms} />
            </div>

            {/* Create Post + Post Queue */}
            <CreatePostForm
              exerciseId={selectedExercise}
              platforms={platforms}
              onCreated={() => loadExerciseDetails(selectedExercise)}
            />

            <PostQueue
              posts={postQueue}
              platforms={platforms}
              onPublish={handlePublishPost}
              publishingId={publishingId}
            />
          </div>
        )}

        {/* Empty state - no exercise selected */}
        {!selectedExercise && !loading && (
          <div className="p-16 text-center border rounded-xl bg-muted/10">
            <Radio className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-semibold mb-1">Media Portal Dashboard</h3>
            <p className="text-sm text-muted-foreground">
              Select an exercise above to view and manage its media simulation.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MediaPortalPage;
